import { useI18n } from "@/hooks/useI18n";
import { 
  ShoppingCart, 
  Users, 
  Thermometer, 
  Package, 
  Plane, 
  FileText, 
  Warehouse, 
  Truck 
} from "lucide-react";

export const SmartConsolidation = () => {
  const { t } = useI18n();

  const steps = [
    { icon: ShoppingCart, key: 'consolidation.step1', color: 'text-accent' },
    { icon: Users, key: 'consolidation.step2', color: 'text-andean-sage' },
    { icon: Thermometer, key: 'consolidation.step3', color: 'text-blue-500' },
    { icon: Package, key: 'consolidation.step4', color: 'text-accent' },
    { icon: Plane, key: 'consolidation.step5', color: 'text-andean-forest' },
    { icon: FileText, key: 'consolidation.step6', color: 'text-andean-sage' },
    { icon: Warehouse, key: 'consolidation.step7', color: 'text-blue-500' },
    { icon: Truck, key: 'consolidation.step8', color: 'text-accent' },
  ];

  return (
    <section className="py-20 bg-gradient-mist">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="font-display text-2xl md:text-3xl font-light text-primary mb-6 tracking-wide">
            {t('consolidation.title')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From farm to worldwide destinations in 8 seamless steps with guaranteed cold-chain logistics
          </p>
        </div>

        {/* Desktop flow */}
        <div className="hidden lg:block relative">
          <div className="flex items-center justify-between mb-8">
            {steps.map((step, index) => (
              <div key={step.key} className="flex flex-col items-center relative">
                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div className="absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-andean-sage to-andean-mist transform translate-x-1/2 z-0" />
                )}
                
                {/* Step circle */}
                <div className="w-16 h-16 bg-card rounded-full border-2 border-border shadow-soft flex items-center justify-center mb-4 relative z-10 group hover:shadow-gold transition-all duration-300">
                  <step.icon className={`w-8 h-8 ${step.color} group-hover:scale-110 transition-transform duration-300`} />
                </div>
                
                {/* Step label */}
                <p className="text-sm font-medium text-center text-primary max-w-20">
                  {t(step.key)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile flow */}
        <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-6">
          {steps.map((step, index) => (
            <div key={step.key} className="flex items-center gap-4 trust-badge">
              <div className="w-12 h-12 bg-gradient-gold rounded-xl flex items-center justify-center flex-shrink-0">
                <step.icon className="w-6 h-6 text-andean-forest" />
              </div>
              <div>
                <p className="font-medium text-primary">{index + 1}. {t(step.key)}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Key features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
          <div className="trust-badge text-center">
            <Thermometer className="w-10 h-10 text-blue-500 mx-auto mb-4" />
            <h3 className="font-light text-primary mb-2 tracking-wide">0-4°C Cold Chain</h3>
            <p className="text-sm text-muted-foreground font-light">Temperature monitored from farm to delivery</p>
          </div>
          <div className="trust-badge text-center">
            <FileText className="w-10 h-10 text-andean-sage mx-auto mb-4" />
            <h3 className="font-light text-primary mb-2 tracking-wide">Complete Documentation</h3>
            <p className="text-sm text-muted-foreground font-light">Phyto, Agrocalidad, DUA, AWB included</p>
          </div>
          <div className="trust-badge text-center">
            <Plane className="w-10 h-10 text-accent mx-auto mb-4" />
            <h3 className="font-light text-primary mb-2 tracking-wide">Worldwide Delivery</h3>
            <p className="text-sm text-muted-foreground font-light">&lt;2h cold-room window</p>
          </div>
        </div>
      </div>
    </section>
  );
};